import { StackedModal } from "@/components/modal/stacked-modal";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, RefObject } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

const INGREDIENTS_PLACEHOLDER = `For example:

150g chicken breast (raw)
75g dry pasta (uncooked)
1 tbsp olive oil (for cooking)
200g tomato sauce

Tips for best accuracy:

Include quantities for each ingredient (g, ml, tbsp, slices etc.)

Say raw or cooked where relevant

Include oils, butter, sauces and spreads used

Be specific about ingredients (e.g. chicken breast vs thigh, whole milk vs semi-skimmed, cheddar vs mozzarella)

Adding a packaged item? Photograph the label instead 📷`;

const METHOD_PLACEHOLDER = `For example:

1. Boil the pasta according to packet instructions
2. Heat the sauce in a pan
3. Drain pasta and mix with sauce`;

export const INGREDIENTS_PLACEHOLDER_TEXT = INGREDIENTS_PLACEHOLDER;
export const METHOD_PLACEHOLDER_TEXT = METHOD_PLACEHOLDER;

interface AddRecipeTextareaPageProps<T extends FieldValues> {
  pageRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  control: Control<T>;
  name: Path<T>;
  subtitle: string;
  placeholder?: string;
}

/**
 * Reusable stacked modal page for free-text input (ingredients or method).
 * Same textarea style as the main Add your food flow; used for "Add ingredients" and "Add method" sub-pages.
 */
export const AddRecipeTextareaPage = memo(function AddRecipeTextareaPage<T extends FieldValues>({
  pageRef,
  isOpen,
  onDismiss,
  onConfirm,
  control,
  name,
  subtitle,
  placeholder = INGREDIENTS_PLACEHOLDER,
}: AddRecipeTextareaPageProps<T>) {
  return (
    <StackedModal
      pageRef={pageRef}
      isOpen={isOpen}
      title="Add your food"
      subtitle={subtitle}
      showHeader={true}
      showConfirmButton={true}
      onConfirm={onConfirm}
      onDidDismiss={onDismiss}
    >
      <div className="safe-area-bottom flex h-full flex-col">
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <textarea
              {...field}
              placeholder={placeholder}
              value={field.value ?? ""}
              className={cn(textVariants.body, "h-full min-h-[200px] w-full resize-none p-4")}
            />
          )}
        />
      </div>
    </StackedModal>
  );
}) as <T extends FieldValues>(props: AddRecipeTextareaPageProps<T>) => JSX.Element;
